import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Container, Row, Col, Input, InputGroup, FormGroup, Label, Form, Alert } from 'reactstrap'
import '../../assets/scss/custom/react-credit-cards/default.scss'
import { dispatchSweetAlert } from '../../actions'
import CheckBox from '../general/CheckBox'
import { setPaymentMethod, setCcData, setPoNumber, setNet30Confirmed } from './checkoutFunctions'
import { validateMinimumCreditCardTotal, getMinimumCreditCardValidationMessage } from './utils'

const CheckoutCardPanel = props => {
  if (props?.youPay <= 0) return null

  // Check Promotions for set_payment_to_net_30 and set appropriately
  const [checked, setChecked] = useState({ net30: false })
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState('authorize_net')
  const colStyle = { display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }
  const rowStyle = { cursor: 'pointer', borderRadius: '3px' }

  // Check if minimum credit card validation should be shown
  const showMinimumCreditCardValidation = validateMinimumCreditCardTotal(props.youPay, props.paymentMethod)
  const validationMessage = getMinimumCreditCardValidationMessage()

  useEffect(() => {
    const promoArray = props.appliedPromotions
    const shouldShowNet30 = props.customerGroup.id && props.customerGroup.net_30_payments
    const isCreditCardOnly = !props.customerGroup.hide_credit_card && !shouldShowNet30

    // if any promotion sets net 30, keep checkout reducer updated
    // Only set to net30 if no payment method is currently selected (to avoid overriding user choice)
    if (promoArray.some(promo => promo.set_payment_to_net_30 === true)) {
      // If there's a promotion that forces net30, set it regardless of current payment method
      // This handles the case where promotions load after the component mounts
      props.setPaymentMethod('net30')
      props.setNet30Confirmed(true)
    } else if (isCreditCardOnly && !props.paymentMethod) {
      // If credit card is the only option and no payment method is set, select credit card
      props.setPaymentMethod('credit_card')
    } else if (!props.customerGroup.hide_credit_card && !props.paymentMethod) {
      // Set credit card as default payment method if not hidden and no method is set
      props.setPaymentMethod('credit_card')
    }
  }, [props.appliedPromotions, props.customerGroup.hide_credit_card, props.customerGroup.net_30_payments]) // Added net_30_payments to dependencies

  // If there is an extra field named po_number, set it in the to extra field value. Otherwise, use props.poNumber
  // const [poNumber, setPoNumber] = useState('')
  useEffect(() => {
    if (props.extraFields) {
      props.extraFields.map((key, value) => {
        if (key.field === 'po_number') {
          props.setPoNumber(key.value)
        }
      })
    } else {
      if (props.poNumber) {
        props.setPoNumber(props.poNumber)
      }
    }
  }, [props.extraFields])

  const handlePoNumberChange = event => {
    props.setPoNumber(event.target.value)
  }

  const handleCheckBox = val => {
    setChecked({ ...checked, [val]: !checked[val] })
    if (val === 'net30') {
      handleChangePaymentMethod(!checked[val] && 'net30')
    }
  }

  const handleChangePaymentMethod = e => {
    props.setPaymentMethod(e)
    if (e === 'net30') {
      // confirmNet30()
      props.setNet30Confirmed(true)
    } else {
      // remove net 30
      confirmNet30(false)
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    // expiry needs some specific handling to concatenate MM & YY
    if (name === 'expiry_yy' || name === 'expiry_mm') {
      const expiry = props.ccData.card_expiry
      const mm = expiry.substr(0, 2)
      const yy = expiry.substr(2)
      let newExpiry
      if (name == 'expiry_mm') {
        newExpiry = `${value}${yy}`
      } else if (name == 'expiry_yy') {
        newExpiry = `${mm}${value}`
      }
      props.setCcData({ card_expiry: newExpiry })
    } else {
      // handling for all other fields:
      props.setCcData({ [name]: value })
    }
  }
  const handleInputFocus = e => {
    setFocus(e.target.name)
  }
  const setFocus = fieldName => {
    // send to prop for cards: focused = name|number|expiry|cvc
    let propValue
    if (fieldName === 'card_number') {
      propValue = 'number'
    } else if (fieldName === 'card_name') {
      propValue = 'name'
    } else if (fieldName === 'expiry_mm' || fieldName === 'expiry_yy') {
      propValue = 'expiry'
    } else if (fieldName === 'card_cvc') {
      propValue = 'cvc'
    }

    props.setCcData({ focus: propValue })
  }

  const CcInputField = (name, label, type) => {
    return (
      <Col sm={name === 'card_cvc' ? 4 : 12} className="mf-px-0">
        <Input
          type={type}
          name={name}
          placeholder={label}
          onChange={handleChange}
          onFocus={handleInputFocus}
          value={props.ccData[name]}
        />
      </Col>
    )
  }

  const CcForm = () => {
    return (
      <Form autoComplete="off" className="cc-form-flex mf-mt-3">
        <FormGroup
          row
          style={{
            width: '100%'
          }}
        >
          {CcInputField('card_number', 'Card Number', 'tel', { required: true })}
        </FormGroup>

        <FormGroup
          row
          style={{
            width: '100%'
          }}
        >
          {CcInputField('card_name', 'Full Name', 'text', { required: true })}
        </FormGroup>

        <FormGroup
          row
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap'
          }}
        >
          <Col sm={12}>
            <InputGroup style={{ flexWrap: 'nowrap' }}>
              <Input
                type="select"
                name="expiry_mm"
                required
                onChange={handleChange}
                onFocus={handleInputFocus}
                className="cc-exp-date col-4"
              >
                <option value="**">MM</option>
                {[...new Array(12).keys()].map(month => {
                  const mm = `${month + 1}`.padStart(2, '0')
                  return (
                    <option key={month} value={mm}>
                      {mm}
                    </option>
                  )
                })}
              </Input>

              <Input
                type="select"
                name="expiry_yy"
                required
                onChange={handleChange}
                onFocus={handleInputFocus}
                className="cc-exp-date col-4"
                style={{
                  marginRight: '10px',
                  borderRadius: '0 4px 4px 0'
                }}
              >
                <option value="**">YY</option>
                {[...new Array(15).keys()].map(year => {
                  const yyyy = year + new Date().getFullYear()
                  const yy = yyyy.toString().substr(2, 3)
                  return (
                    <option key={year} value={yy}>
                      {yy}
                    </option>
                  )
                })}
              </Input>
              {CcInputField('card_cvc', 'CVC', 'tel', { required: true })}
            </InputGroup>
          </Col>
        </FormGroup>

        {props.errors.length > 0 ? (
          <div className="danger" role="alert">
            <div className="danger-color">{props.errors}</div>
          </div>
        ) : null}
      </Form>
    )
  }

  const confirmNet30 = (confirm = true) => {
    if (confirm) {
      props.dispatchSweetAlert({
        alertMessage:
          'By selecting this payment option I confirm that my order will be billed according to the net-payment terms established for this e-store.',
        showCancel: false,
        confirmBtnCssClass: 'mf-secure-co-btn',
        onConfirm: () => props.setNet30Confirmed(true)
      })
    } else {
      props.setNet30Confirmed(false)
    }
  }

  const shouldShowNet30 = props.customerGroup.id && props.customerGroup.net_30_payments

  if (props.customerGroup.hide_credit_card === true && shouldShowNet30) {
    return (
      <div className="mf-m-0 mf-mb-3 box-shadow white-background">
        <div className="ckout-header">
          <h5 className="mf-m-0">Payment Method</h5>
        </div>
        {shouldShowNet30 && (
          <Container className="mf-mb-20">
            <Row>
              <Col xs="12" className="px-30">
                <Row
                  style={rowStyle}
                  className={
                    checked.net30 ? ' alert-success cart-flex mf-pt-3 mf-pb-3 mf-border' : ' cart-flex mf-pt-3 mf-pb-3 mf-border'
                  }
                  onClick={() => handleCheckBox('net30')}
                >
                  <Col sm="10">
                    <h6>{props.netTerm || 'Net 30'}</h6>
                  </Col>
                  <Col sm="2" style={colStyle}>
                    <CheckBox checked={checked.net30} onChange={() => handleCheckBox('net30')} />
                  </Col>
                </Row>
                {checked.net30 && (
                  <Row style={rowStyle} className="cart-flex mf-pt-3 mf-pb-3 mf-border">
                    <Col>
                      <Label for="po_number">
                        <strong>
                          {props.netTermPoNumber ? `${props.netTermPoNumber} (Optional)` : 'P.O. Number (Optional):'}
                        </strong>
                      </Label>
                    </Col>
                    <Col sm={9}>
                      <Input name="po_number" value={props.poNumber} onChange={handlePoNumberChange} maxLength={25} />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Container>
        )}
      </div>
    )
  } else {
    if ((props.customerGroup.hide_credit_card === true && !shouldShowNet30) || props.youPay === 0) {
      return null
    } else {
      return (
        <>
          {/* First Payment Method section */}
          <div className="mf-m-0 box-shadow white-background mf-mb-3">
            <div className="ckout-header">
              <h5 className="mf-m-0">Payment Method</h5>
            </div>
            <div style={{ padding: '20px' }}>
              {/* Minimum Credit Card Validation Message */}
              {showMinimumCreditCardValidation && (
                <Alert color="warning" className="mf-mb-3">
                  <strong>{validationMessage.title}</strong>
                  <br />
                  {validationMessage.message}
                </Alert>
              )}

              {/* Radio button selection */}
              <div
                style={{
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'baseline'
                }}
              >
                <input
                  type="radio"
                  id="credit_card_radio"
                  name="payment_method_radio"
                  value="credit_card"
                  checked={props.paymentMethod === 'credit_card'}
                  onChange={() => handleChangePaymentMethod('credit_card')}
                  style={{ marginRight: '10px' }}
                />
                <label htmlFor="credit_card_radio">
                  <strong>Credit Card</strong>
                  <br /> You'll enter your card details on the next screen after clicking the{' '}
                  <strong>Place Your Order</strong> button.
                </label>
              </div>

              {shouldShowNet30 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline'
                  }}
                >
                  <input
                    type="radio"
                    id="net30_radio"
                    name="payment_method_radio"
                    value="net30"
                    checked={props.paymentMethod === 'net30'}
                    onChange={() => handleChangePaymentMethod('net30')}
                    style={{ marginRight: '10px' }}
                  />
                  <label htmlFor="net30_radio">
                    <strong>{props.netTerm || 'Net 30'}</strong>
                    <br /> By selecting this payment option, I confirm that my order will be billed according to the
                    net-payment terms established for this e-store.
                  </label>
                </div>
              )}

              {props.paymentMethod === 'net30' && shouldShowNet30 && (
                <div style={{ marginTop: '20px' }}>
                  <Container>
                    <Row>
                      <Col sm={12} md={9} className="mf-p-0">
                        <Label for="po_number">
                          <strong>
                            {props.netTermPoNumber ? `${props.netTermPoNumber} (Optional)` : 'P.O. Number (Optional):'}
                          </strong>
                        </Label>
                        <Input name="po_number" value={props.poNumber} onChange={handlePoNumberChange} maxLength={25} />
                      </Col>
                    </Row>
                  </Container>
                </div>
              )}
            </div>
          </div>
        </>
      )
    }
  }
}

const mapStateToProps = state => {
  return {
    paymentMethod: state.checkout.paymentMethod,
    youPay: state.checkout.youPay,
    errors: state.checkout.errors,
    ccData: state.checkout.ccData,
    customerGroup: state.customerGroup,
    poNumber: state.checkout.poNumber,
    appliedPromotions: state.checkout.appliedPromotions,
    currentUser: state.currentUser,
    netTerm: state.portal.context_dictionary.net_term_def,
    netTermPoNumber: state.portal.context_dictionary.net_term_po_number
  }
}

export default connect(mapStateToProps, {
  setPaymentMethod,
  setCcData,
  dispatchSweetAlert,
  setPoNumber,
  setNet30Confirmed
})(CheckoutCardPanel)
